import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarPicker } from './ui/calendar';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Calendar, X } from 'lucide-react';

// Locale de formatage des dates, dérivée de la langue choisie par l'utilisateur
// (évite d'afficher des dates françaises à un visiteur anglais/japonais).
export function dateLocale(l) {
  l = (l || 'en').toLowerCase();
  if (l.indexOf('ja') === 0 || l.indexOf('jp') === 0) return 'ja-JP';
  if (l.indexOf('fr') === 0) return 'fr-FR';
  return 'en-US';
}

export function toDateStr(d) {
  var y = d.getFullYear();
  var m = ('0' + (d.getMonth() + 1)).slice(-2);
  var day = ('0' + d.getDate()).slice(-2);
  return y + '-' + m + '-' + day;
}

// Sélecteur de date de l'application : le calendrier maison, JAMAIS l'input
// natif du navigateur. Utilisé côté guide (fenêtre de disponibilité) comme côté
// voyageur (recherche), pour que la même gestuelle vaille partout.
//
// `minDate` interdit les jours antérieurs (ex. une date de fin avant le début)
// et fait ouvrir le calendrier sur le bon mois : sans ça, il s'ouvre sur le mois
// courant et un clic sur « 12 » enregistre le 12 du mois en cours au lieu du
// mois voulu — erreur réellement survenue en production (12 août au lieu du
// 12 décembre), qui rendait l'activité publiée mais inréservable.
export default function DatePicker({ value, onChange, placeholder, minDate, clearable, testId }) {
  var { t, i18n } = useTranslation();
  var [open, setOpen] = React.useState(false);
  var showClear = value && clearable !== false;

  function fmt(s) {
    if (!s) return placeholder || t('hostDash.form.datePlaceholder');
    try {
      var d = new Date(s + 'T00:00:00');
      return d.toLocaleDateString(dateLocale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) { return s; }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={'w-full flex items-center gap-2 bg-black/20 border border-white/10 rounded-md py-2.5 px-3 text-left hover:border-[#F9004D]/60 transition-colors ' + (value ? 'text-white' : 'text-zinc-400')}
        >
          <Calendar className="h-4 w-4 text-[#F9004D] shrink-0" />
          <span className="text-sm">{fmt(value)}</span>
          {showClear && (
            <span onClick={function(e) { e.stopPropagation(); onChange(''); }} className="ml-auto text-zinc-500 hover:text-white" role="button">
              <X className="h-4 w-4" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-zinc-900 border-white/10 text-white">
        <CalendarPicker
          mode="single"
          selected={value ? new Date(value + 'T00:00:00') : undefined}
          defaultMonth={value ? new Date(value + 'T00:00:00')
                              : (minDate ? new Date(minDate + 'T00:00:00') : undefined)}
          disabled={minDate ? { before: new Date(minDate + 'T00:00:00') } : undefined}
          onSelect={function(d) { if (d) { onChange(toDateStr(d)); setOpen(false); } }}
          initialFocus
          classNames={{
            months: 'flex flex-col', month: 'space-y-4 p-2',
            caption: 'flex justify-center pt-1 relative items-center mb-2',
            caption_label: 'text-base font-bold text-white',
            nav_button: 'h-8 w-8 bg-zinc-800 hover:bg-zinc-700 rounded-md text-white flex items-center justify-center',
            nav_button_previous: 'absolute left-1', nav_button_next: 'absolute right-1',
            head_cell: 'text-zinc-400 w-10 font-normal text-sm',
            cell: 'relative p-0 text-center',
            day: 'h-10 w-10 p-0 font-normal text-sm rounded-md text-zinc-200 hover:bg-zinc-700 aria-selected:opacity-100',
            day_selected: 'bg-[#F9004D] text-white hover:bg-[#F9004D] focus:bg-[#F9004D]',
            day_today: 'bg-zinc-800 text-[#00F0FF] font-bold',
            day_disabled: 'text-zinc-600 opacity-40 cursor-not-allowed'
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
